import { useEffect, useMemo, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { FilePdf, MagnifyingGlass, Download, FloppyDisk, ArrowCounterClockwise } from "@phosphor-icons/react";

// Keep in sync with backend `_EXPORT_COLUMNS` order.
export const EXPORT_COLUMNS = [
  { key: "name",          label: "Job No." },
  { key: "title",         label: "Project Name" },
  { key: "client_name",   label: "Client" },
  { key: "category",      label: "Type (Civil/Interior)" },
  { key: "status",        label: "Status" },
  { key: "manager_name",  label: "Project Manager" },
  { key: "start_date",    label: "Start Date" },
  { key: "end_date",      label: "End Date" },
  { key: "total_fee",     label: "Total Fee" },
  { key: "advance_total", label: "Advance Paid" },
  { key: "collected",     label: "Collected" },
  { key: "outstanding",   label: "Dues (Outstanding)" },
];
const DEFAULT_COLS = ["name", "title", "client_name", "category", "status",
                      "manager_name", "start_date", "total_fee", "advance_total", "outstanding"];
const DEFAULTS = {
  columns: DEFAULT_COLS,
  category: "all",
  status: "all",
  include_totals: true,
  title: "Projects Report",
};

const presetKey = (userId) => `dhp_projects_export_preset_${userId || "anon"}`;

function readPreset(userId) {
  try {
    const raw = localStorage.getItem(presetKey(userId));
    if (!raw) return null;
    const p = JSON.parse(raw);
    // Sanitize columns against the current whitelist to tolerate future removals.
    const allowed = new Set(EXPORT_COLUMNS.map((c) => c.key));
    const cols = Array.isArray(p.columns) ? p.columns.filter((c) => allowed.has(c)) : null;
    return {
      columns: cols && cols.length ? cols : DEFAULTS.columns,
      category: ["all", "civil", "interior"].includes(p.category) ? p.category : DEFAULTS.category,
      status: ["all", "active", "on_hold", "completed", "cancelled"].includes(p.status) ? p.status : DEFAULTS.status,
      include_totals: typeof p.include_totals === "boolean" ? p.include_totals : DEFAULTS.include_totals,
      title: typeof p.title === "string" && p.title.trim() ? p.title : DEFAULTS.title,
    };
  } catch { return null; }
}

function writePreset(userId, preset) {
  try { localStorage.setItem(presetKey(userId), JSON.stringify(preset)); } catch { /* quota / privacy mode */ }
}

function clearPreset(userId) {
  try { localStorage.removeItem(presetKey(userId)); } catch { /* ignore */ }
}

/**
 * Reusable "Export Projects → PDF" dialog.
 *
 * Props:
 *  - open, onOpenChange            : controlled visibility
 *  - projects                      : pre-fetched array of projects (ProjectsPage already has them).
 *                                    When omitted, the dialog fetches `/projects` itself.
 *  - initialCategory               : "all" | "civil" | "interior"
 *  - initialSelectedNames          : optional preselected project names
 */
export default function ProjectsExportDialog({
  open, onOpenChange, projects: externalProjects,
  initialCategory = "all", initialSelectedNames = null,
}) {
  const { user } = useAuth();
  const uid = user?.id;

  const [projects, setProjects] = useState(externalProjects || []);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Lazy-init from a saved preset so first paint doesn't flash the defaults.
  const initial = () => readPreset(uid) || DEFAULTS;
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(() => initial().category);
  const [status, setStatus] = useState(() => initial().status);
  const [columns, setColumns] = useState(() => initial().columns);
  const [includeTotals, setIncludeTotals] = useState(() => initial().include_totals);
  const [title, setTitle] = useState(() => initial().title);
  const [hasPreset, setHasPreset] = useState(() => !!readPreset(uid));
  const [selected, setSelected] = useState(() =>
    new Set(initialSelectedNames || [])
  );

  // Fetch if not provided
  useEffect(() => {
    if (!open) return;
    if (externalProjects && externalProjects.length) {
      setProjects(externalProjects);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await http.get("/projects");
        setProjects(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, externalProjects]);

  // Reset per-session fields on open and re-apply the saved preset (if any).
  // Explicit prop overrides (initialCategory / initialSelectedNames) win over the
  // preset only when there's no saved preset for this user — the preset is the
  // user's "one-click" default once they've stored one.
  useEffect(() => {
    if (!open) return;
    const p = readPreset(uid);
    setHasPreset(!!p);
    if (p) {
      setColumns(p.columns);
      setCategory(p.category);
      setStatus(p.status);
      setIncludeTotals(p.include_totals);
      setTitle(p.title);
    } else {
      setColumns(DEFAULTS.columns);
      setCategory(initialCategory || DEFAULTS.category);
      setStatus(DEFAULTS.status);
      setIncludeTotals(DEFAULTS.include_totals);
      setTitle(DEFAULTS.title);
    }
    setSelected(new Set(initialSelectedNames || []));
    setSearch("");
  }, [open, uid, initialCategory, initialSelectedNames]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (category !== "all" && (p.category || "civil") !== category) return false;
      if (status !== "all" && (p.status || "active") !== status) return false;
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.client_name || "").toLowerCase().includes(q) ||
        (p.title || "").toLowerCase().includes(q)
      );
    });
  }, [projects, category, status, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.name));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allFilteredSelected) filtered.forEach((p) => next.delete(p.name));
    else filtered.forEach((p) => next.add(p.name));
    setSelected(next);
  };
  const toggleOne = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    setSelected(next);
  };
  const toggleColumn = (key) => {
    setColumns((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    if (columns.length === 0) {
      toast.error("Pick at least one column to include.");
      return;
    }
    const useSelection = selected.size > 0;
    const payload = {
      columns,
      include_totals: includeTotals,
      title: title.trim() || "Projects Report",
      ...(useSelection
        ? { project_names: Array.from(selected) }
        : {
            category: category === "all" ? null : category,
            status: status === "all" ? null : status,
          }),
    };
    setDownloading(true);
    try {
      const res = await http.post("/projects/export-pdf", payload, { responseType: "blob" });
      // Download the blob
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Projects-Report-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // Persist this configuration as the user's default for next time.
      writePreset(uid, {
        columns, category, status,
        include_totals: includeTotals,
        title: title.trim() || DEFAULTS.title,
      });
      setHasPreset(true);
      toast.success(`Exported ${useSelection ? selected.size : filtered.length} project(s). Saved as your default.`);
      onOpenChange?.(false);
    } catch (e) {
      // Blob responses need text() to read error body
      let msg = formatApiError(e);
      try {
        if (e?.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          const parsed = JSON.parse(t);
          if (parsed?.detail) msg = typeof parsed.detail === "string" ? parsed.detail : msg;
        }
      } catch { /* keep default msg */ }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const handleSavePreset = () => {
    writePreset(uid, {
      columns, category, status,
      include_totals: includeTotals,
      title: title.trim() || DEFAULTS.title,
    });
    setHasPreset(true);
    toast.success("Saved as your default export preset.");
  };

  const handleResetPreset = () => {
    clearPreset(uid);
    setHasPreset(false);
    setColumns(DEFAULTS.columns);
    setCategory(DEFAULTS.category);
    setStatus(DEFAULTS.status);
    setIncludeTotals(DEFAULTS.include_totals);
    setTitle(DEFAULTS.title);
    toast.message("Preset cleared — showing defaults.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="projects-export-dialog"
        className="max-w-5xl w-[96vw] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePdf size={22} weight="duotone" className="text-amber-700" />
            Export Projects to PDF
          </DialogTitle>
        </DialogHeader>

        {hasPreset && (
          <div
            data-testid="export-preset-banner"
            className="mt-2 flex items-center justify-between gap-2 border border-amber-200 bg-amber-50 px-3 py-2 text-xs"
          >
            <span className="flex items-center gap-2 text-amber-900">
              <FloppyDisk size={14} weight="duotone" />
              Using your saved preset. Changes auto-save when you export.
            </span>
            <button
              type="button"
              data-testid="export-reset-preset"
              onClick={handleResetPreset}
              className="inline-flex items-center gap-1 text-amber-800 hover:underline"
            >
              <ArrowCounterClockwise size={12} weight="bold" />
              Reset to defaults
            </button>
          </div>
        )}

        {/* Row 1: Report title + filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-2">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">Report Title</label>
            <Input
              data-testid="export-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Projects Report"
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="export-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="civil">Civil</SelectItem>
                <SelectItem value="interior">Interior</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="export-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Columns picker */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500">Columns to Include</label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="export-cols-all"
                onClick={() => setColumns(EXPORT_COLUMNS.map((c) => c.key))}
                className="text-xs text-amber-700 hover:underline"
              >Select all</button>
              <span className="text-zinc-300">·</span>
              <button
                type="button"
                data-testid="export-cols-none"
                onClick={() => setColumns([])}
                className="text-xs text-amber-700 hover:underline"
              >Clear</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 border border-zinc-200 rounded p-3 bg-zinc-50">
            {EXPORT_COLUMNS.map((c) => (
              <label
                key={c.key}
                data-testid={`export-col-${c.key}`}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={columns.includes(c.key)}
                  onCheckedChange={() => toggleColumn(c.key)}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>

        {/* Totals toggle */}
        <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
          <Checkbox
            data-testid="export-include-totals"
            checked={includeTotals}
            onCheckedChange={(v) => setIncludeTotals(!!v)}
          />
          Include a totals row (sum of Total Fee / Advance / Collected / Dues)
        </label>

        {/* Project picker */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500">
              Projects&nbsp;
              <span className="normal-case font-normal text-zinc-400">
                {selected.size > 0
                  ? `(${selected.size} selected — these override filters)`
                  : `(no row selected → the filters above decide what's exported, ${filtered.length} match)`}
              </span>
            </label>
            {selected.size > 0 && (
              <button
                type="button"
                data-testid="export-clear-selection"
                onClick={() => setSelected(new Set())}
                className="text-xs text-amber-700 hover:underline"
              >
                Clear selection
              </button>
            )}
          </div>
          <div className="relative mb-2">
            <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="export-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Job No., Client, or Project Name..."
              className="pl-8"
            />
          </div>
          <div className="border border-zinc-200 rounded max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-zinc-200 z-[1]">
                <tr>
                  <th className="p-2 w-10 text-left">
                    <Checkbox
                      data-testid="export-select-all"
                      checked={allFilteredSelected}
                      onCheckedChange={toggleAll}
                    />
                  </th>
                  <th className="p-2 text-left text-xs uppercase tracking-wider text-zinc-500">Job No.</th>
                  <th className="p-2 text-left text-xs uppercase tracking-wider text-zinc-500">Project</th>
                  <th className="p-2 text-left text-xs uppercase tracking-wider text-zinc-500">Client</th>
                  <th className="p-2 text-left text-xs uppercase tracking-wider text-zinc-500">Type</th>
                  <th className="p-2 text-left text-xs uppercase tracking-wider text-zinc-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="p-4 text-center text-zinc-400">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-zinc-400">No projects match your filters.</td></tr>
                )}
                {!loading && filtered.map((p) => (
                  <tr
                    key={p.name}
                    data-testid={`export-row-${p.name}`}
                    className={`border-b border-zinc-100 hover:bg-amber-50 cursor-pointer ${selected.has(p.name) ? "bg-amber-50" : ""}`}
                    onClick={() => toggleOne(p.name)}
                  >
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(p.name)}
                        onCheckedChange={() => toggleOne(p.name)}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">{p.name}</td>
                    <td className="p-2">{p.title || "—"}</td>
                    <td className="p-2">{p.client_name || "—"}</td>
                    <td className="p-2 capitalize">{p.category || "civil"}</td>
                    <td className="p-2 capitalize">{(p.status || "active").replace("_", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter className="mt-5 gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleSavePreset}
            data-testid="export-save-preset"
            className="gap-2"
            title="Save the current columns + filters as your one-click default"
          >
            <FloppyDisk size={14} weight="bold" />
            Save as my default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="export-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={downloading || columns.length === 0}
              data-testid="export-submit"
              className="bg-amber-700 hover:bg-amber-800 text-white gap-2"
            >
              <Download size={16} weight="bold" />
              {downloading ? "Preparing PDF…" : "Export PDF"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
